/**
 * useVoice — thin wrapper around browser Web Speech API.
 *
 * Design intent:
 * - Tap-to-talk: caller controls start/stop; no continuous listening.
 * - Interim transcripts stream into a local buffer; final transcript is
 *   handed off through onFinal so the caller decides whether to send.
 * - If SpeechRecognition is unavailable, `supported === false` and start()
 *   is a no-op. Voice never blocks the app.
 * - Confidential mode is enforced by the caller (pass `disabled={true}`).
 *   This hook itself is policy-agnostic.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";

function getSR() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function useVoice({ onFinal, onInterim, lang = "en-US" } = {}) {
  const SR = useMemo(getSR, []);
  const supported = !!SR;
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const bufferRef = useRef("");

  const teardown = useCallback(() => {
    const r = recRef.current;
    if (r) {
      try { r.onresult = null; r.onerror = null; r.onend = null; r.stop(); } catch {}
    }
    recRef.current = null;
    setListening(false);
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const start = useCallback(() => {
    if (!supported || listening) return;
    setError(null);
    bufferRef.current = "";
    const rec = new SR();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (ev) => {
      let final = "";
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) final += res[0]?.transcript || "";
        else interim += res[0]?.transcript || "";
      }
      if (final) {
        bufferRef.current += final;
        onFinal?.(bufferRef.current.trim());
      }
      if (interim) onInterim?.(interim);
    };
    rec.onerror = (ev) => {
      // Only surface meaningful errors; ignore 'no-speech' style noise.
      const code = ev?.error || "unknown";
      if (code !== "no-speech" && code !== "aborted") setError(code);
    };
    rec.onend = () => {
      setListening(false);
      recRef.current = null;
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
      setError("failed_to_start");
    }
  }, [SR, listening, supported, lang, onFinal, onInterim]);

  const stop = useCallback(() => {
    teardown();
  }, [teardown]);

  return { supported, listening, error, start, stop };
}
